import React from 'react';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { ChatPlayTTSIcon } from 'public/assets';
import { Statement } from '@/customTypes/chatStory';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import TextWithEffect from './TextWithEffect';

interface IChatItem {
  statement: Statement;
  isShowProfile?: boolean;
  profile?: string;
}

const MainCharacterMessage = ({
  statement,
  onPlayTTS,
}: {
  statement: Statement;
  onPlayTTS: (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => void;
}) => {
  return (
    <div
      className={`message-container`}
      css={
        statement.statementType === 'MainCharacterTalk'
          ? messageCSS(statement)
          : thinkCSS(statement)
      }
    >
      {statement.textOptions && (
        <>
          <div className="icon-click" onClick={onPlayTTS} />
          <div className="icon">
            <ChatPlayTTSIcon />
          </div>
        </>
      )}
      <div className="message">
        <TextWithEffect fullWidthText={statement} />
      </div>
    </div>
  );
};

const MainCharacter = ({
  statement,
  isShowProfile = true,
  profile,
}: IChatItem) => {
  const { onPlayTTSStatement } = useChatPlayContext();

  const onPlayTTS = (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
    if (statement.textOptions) {
      onPlayTTSStatement(statement);
      e.stopPropagation();
    }
  };

  return (
    <div css={talkItem(statement.effect?.scriptBackgroundColor)}>
      {isShowProfile ? (
        <div className="main-message">
          <div className="content">
            <div className="name">{statement?.chrName || ''}</div>
            {statement.statementType === 'MainCharacterMessageImage' ? (
              <img
                css={imageCSS}
                src={statement?.link}
                alt="MainCharacter message image"
              />
            ) : (
              <MainCharacterMessage
                statement={statement}
                onPlayTTS={onPlayTTS}
              />
            )}
          </div>
          {profile && (
            <img
              className="profile"
              src={profile}
              width={40}
              height={40}
              alt="Profile image"
            />
          )}
        </div>
      ) : (
        <div className={profile && 'only-message'}>
          {statement.statementType === 'MainCharacterMessageImage' ? (
            <div className="content">
              <img
                css={imageCSS}
                src={statement?.link}
                alt="MainCharacter message image"
              />
            </div>
          ) : (
            <MainCharacterMessage statement={statement} onPlayTTS={onPlayTTS} />
          )}
        </div>
      )}
    </div>
  );
};

const talkItem = (scriptBackgroundColor?: string) => css`
  padding: 0 1em;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  background-color: ${scriptBackgroundColor || 'transparent'};

  .item-padding {
    padding: 1em 1em;
  }

  .name {
    font-size: 0.75em;
    color: ${theme.colors.gray900};
    font-weight: 700;
    margin-bottom: 4px;
  }

  .main-message {
    display: flex;
    margin-top: 24px;
  }

  .content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
  }

  .profile {
    flex-shrink: 0;
    width: 2.5em;
    height: 2.5em;
    border-radius: 50%;
    border: none;
    background-color: ${theme.colors.gray500};
    margin-left: 0.5em;
    object-fit: cover;
  }

  .message-container {
    position: relative;
    display: flex;
    margin: 4px 0;
    padding: 0.5em 0.75em;
    border-radius: 0.5em;
    margin-left: 2.5em;
    word-break: break-all;

    .icon-click {
      position: absolute;
      width: 2.5em;
      height: 2.5em;
      top: 0;
      left: 0;
    }

    .icon {
      margin-top: 1px;
      margin-right: 0.5em;
      flex-shrink: 0;
    }
    .message {
      word-break: break-all;
    }
  }

  .only-message {
    margin-right: 3em;
  }
`;

const talkMessage = `
  background-color: ${theme.colors.gray900};
  color: ${theme.colors.white};
`;

const thinkMessage = `
  background-color: none;
  border: 1px solid ${theme.colors.gray400};
  color: ${theme.colors.gray700};
`;

const thinkCSS = (statement: Statement) => css`
  ${thinkMessage}
  background-color: ${statement?.effect?.backgroundColor || 'transparent'};
  border: 1px solid ${statement?.effect?.edgeColor || theme.colors.gray400};
`;

const messageCSS = (statement: Statement) => css`
  ${talkMessage}
  background-color: ${statement?.effect?.backgroundColor};
  border: 1px solid ${statement?.effect?.edgeColor || 'transparent'};
`;

const imageCSS = css`
  display: flex;
  max-width: 70%;
  padding: 0.5em 0;
  object-fit: contain;
  @media screen and (max-width: ${theme.devices.mobile}) {
    max-width: 80%;
  }
`;

export default MainCharacter;
